import { getLanguage } from '@/config.js';

export const getMessage = (messages, messageCode, replacements = null, pluralIndex = null) => {
    const lang = getLanguage();
    const messageRaw =
        typeof pluralIndex === 'number'
            ? messages[lang][messageCode][pluralIndex]
            : messages[lang][messageCode];

    if (typeof messageRaw !== 'string') {
        return '';
    }

    let message = messageRaw;

    if (replacements && typeof replacements === 'object') {
        for (const [placeholder, replacement] of Object.entries(replacements)) {
            message = messageRaw.replaceAll(placeholder, replacement.toString());
        }
    }

    return message;
};

/**
 * @example
 * // localization.json (per-locale plural forms)
 * {
 *     "en": {
 *         "apple": ["apple", "apples"]
 *     }
 * }
 *
 * @param messages
 * @param messageCode
 * @param value
 * @param replacements
 * @returns {string}
 */
export const getMessagePlural = (messages, messageCode, value, replacements = null) => {
    if (typeof value === 'number') {
        if (hasMessage(messages, messageCode, getPluralForm(value))) {
            return getMessage(messages, messageCode, replacements, getPluralForm(value));
        } else {
            return getMessage(messages, messageCode, replacements, 1);
        }
    } else {
        return getMessage(messages, messageCode, replacements);
    }
};

export const hasMessage = (messages, messageCode, pluralIndex = null) => {
    const lang = getLanguage();
    if (typeof pluralIndex === 'number') {
        return typeof messages[lang]?.[messageCode.toString()][pluralIndex] === 'string';
    }

    return typeof messages[lang]?.[messageCode.toString()] === 'string';
};

export const getPluralForm = (value) => {
    let pluralForm;

    if (value < 0) {
        value = -1 * value;
    }

    switch (getLanguage()) {
        case 'ar':
            pluralForm = value !== 1 ? 1 : 0;

            if (value === 0) {
                pluralForm = 0;
            } else if (value === 1) {
                pluralForm = 1;
            } else if (value === 2) {
                pluralForm = 2;
            } else if (value % 100 >= 3 && value % 100 <= 10) {
                pluralForm = 3;
            } else if (value % 100 >= 11) {
                pluralForm = 4;
            } else {
                pluralForm = 5;
            }
            break;

        case 'br':
        case 'fr':
        case 'tr':
            pluralForm = value > 1 ? 1 : 0;
            break;

        case 'de':
        case 'en':
        case 'hi':
        case 'it':
        case 'la':
            pluralForm = value !== 1 ? 1 : 0;
            break;

        case 'ru':
        case 'ua':
            if (value % 10 === 1 && value % 100 !== 11) {
                pluralForm = 0;
            } else if (value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 10 || value % 100 >= 20)) {
                pluralForm = 1;
            } else {
                pluralForm = 2;
            }
            break;

        case 'pl':
            if (value === 1) {
                pluralForm = 0;
            } else if (value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 10 || value % 100 >= 20)) {
                pluralForm = 1;
            } else {
                pluralForm = 2;
            }
            break;

        case 'id':
        case 'ja':
        case 'ms':
        case 'sc':
        case 'tc':
        case 'th':
        case 'vn':
            pluralForm = 0;
            break;

        default:
            pluralForm = 1;
            break;
    }

    return pluralForm;
};
